import { type AgreeToTermsPolicy } from '@logto/schemas';
import { type TFuncKey } from 'i18next';
import { useContext, useMemo, type ReactNode } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import type { Props as TextLinkProps } from '@/components/TextLink';
import TextLink from '@/components/TextLink';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useTerms from '@/hooks/use-terms';
import { cn } from '@/lib/utils';
import DynamicT from '@/shared/components/DynamicT';
import type { Props as PageMetaProps } from '@/shared/components/PageMeta';
import { getBrandingLogoUrl } from '@/shared/utils/logo';

import FirstScreenLayout from '../FirstScreenLayout';

import styles from './index.module.scss';

type Props = {
  readonly children: ReactNode;
  readonly pageMeta: PageMetaProps;
  readonly title: TFuncKey;
  readonly description: string;
  readonly footerTermsDisplayPolicies?: AgreeToTermsPolicy[];
  readonly authOptionsLink: TextLinkProps;
};

/**
 * FocusedAuthPageLayout Component
 *
 * This layout component is designed for focused authentication pages that serve as the first screen
 * for specific auth methods, such as identifier sign-in, identifier-register, and single sign-on landing pages.
 */
const FocusedAuthPageLayout = ({
  children,
  pageMeta,
  title,
  description,
  footerTermsDisplayPolicies = [],
  authOptionsLink,
}: Props) => {
  const { agreeToTermsPolicy } = useTerms();

  const shouldDisplayFooterTerms = useMemo(
    () => agreeToTermsPolicy && footerTermsDisplayPolicies.includes(agreeToTermsPolicy),
    [agreeToTermsPolicy, footerTermsDisplayPolicies]
  );

  const { experienceSettings, theme } = useContext(PageContext);
  if (!experienceSettings) {
    return null;
  }
  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;
  const logo = getBrandingLogoUrl({ theme, branding, isDarkModeEnabled });

  return (
    <FirstScreenLayout pageMeta={pageMeta}>
      {logo && (
        <img
          className={cn(styles.logo, 'self-start -ml-[5px] mb-5 lg:hidden')}
          alt="app logo"
          src={logo}
        />
      )}
      <div className={styles.header}>
        <div className={styles.title}>
          <DynamicT forKey={title} />
        </div>
        <div className={styles.description}>{description}</div>
      </div>
      {children}
      {shouldDisplayFooterTerms && <TermsAndPrivacyLinks className={styles.terms} />}
      <TextLink {...authOptionsLink} className={styles.link} />
    </FirstScreenLayout>
  );
};

export default FocusedAuthPageLayout;
